import type {
  InboxWorkflowItem,
  InboxWorkflowRowOutcomeMap,
  InboxWorkflowState,
  InboxWorkflowStep,
} from "@kontextwerk/control-web-frame/inbox-workflow/contract"
import type { WorkOrderPrintPreviewRow } from "../../../../../../../../../../../../../api/kehrblatt/letters"
import {
  buildPrintRowState,
  impersonalRecipientValue,
  manualRecipientValue,
  objectAddressRecipientValue,
} from "../../../_shared/_logic/print-selection"
import type {
  PrintWorkflowRowBinding,
  WorkOrderPrintRowState,
} from "../../../_shared/_logic/print-types"
import type {
  LetterDeliveryDecision,
  LetterDeliveryDecisionDraft,
  LetterDeliveryDecisionPreviewPayload,
  LetterDeliveryDecisionPreviewRowContext,
  LetterDeliveryDecisionRow,
  LetterDeliverySelection,
  DeliveryEstimateState,
} from "../contract"
import { parseLetterDeliverySelection } from "./preview"
import { automaticDeliveryUnavailableReason } from "../_shared/_logic/02-decide-appointment-letter-delivery-payload"

const DECISION_STEP_KEY = "decide_appointment_letter_delivery"
const SELECTION_STEP_KEY = "select_appointment_letters_for_delivery"

const recordValue = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null

const textValue = (value: unknown): string | null => {
  if (typeof value !== "string") return null

  const normalized = value.trim()

  return normalized || null
}

const parseDecision = (value: unknown): LetterDeliveryDecision | null => {
  const source = recordValue(value)
  const itemId = textValue(source?.letter_delivery_batch_item_id)
  const mode = source?.delivery_mode

  return itemId && (mode === "manual" || mode === "automatic")
    ? {
        letter_delivery_batch_item_id: itemId,
        delivery_mode: mode,
      }
    : null
}

const parseDurableSelection = (value: unknown): LetterDeliverySelection | null => {
  try {
    return parseLetterDeliverySelection(value, "letter_selection")
  } catch {
    return null
  }
}

const projectedStep = (item: InboxWorkflowItem, stepKey: string) =>
  item.steps.find((candidate) => candidate.step_key === stepKey) ?? null

const relevantRequirements = (item: InboxWorkflowItem, step: InboxWorkflowStep) =>
  step.requirements_resolved_by_step.filter((requirement) =>
    item.action_case.requirements.includes(requirement),
  )

const itemIsResolved = (item: InboxWorkflowItem, step: InboxWorkflowStep) => {
  const projected = projectedStep(item, step.step_key)

  if (projected?.status === "resolved") return true

  const requirements = relevantRequirements(item, step)

  return (
    requirements.length > 0 &&
    requirements.every((requirement) =>
      item.action_case.resolved_requirements.includes(requirement),
    )
  )
}

const itemIsOpen = (item: InboxWorkflowItem, step: InboxWorkflowStep) => {
  const projected = projectedStep(item, step.step_key)

  if (projected) return projected.status !== "resolved" && projected.status !== "skipped"

  return relevantRequirements(item, step).some((requirement) =>
    item.unresolved_requirements.includes(requirement),
  )
}

const printRowFromContext = (
  actionCaseId: string,
  context: LetterDeliveryDecisionPreviewRowContext | null,
  selection: LetterDeliverySelection | null,
  previewRow: WorkOrderPrintPreviewRow | null,
): WorkOrderPrintPreviewRow => {
  if (!selection) {
    return {
      ...(previewRow ?? {}),
      work_order_id: previewRow?.work_order_id ?? "",
      property_label: previewRow?.property_label ?? "Vorschaudaten fehlen",
      default_mode: "IMPERSONAL_LETTER",
      available_modes: [],
      recipient_options: [],
    }
  }

  const recipient = selection.recipient
  const manualAddress = recipient.kind === "manual_address" ? recipient.address : null
  const existingRecipient = recipient.kind === "existing_contact" ? recipient : null
  const objectRecipient = recipient.kind === "customer_at_property_address" ? recipient : null

  return {
    ...(previewRow ?? {}),
    work_order_id: previewRow?.work_order_id ?? context?.work_order_id ?? "",
    work_order_label: previewRow?.work_order_label ?? "Ausgewählter Auftrag",
    property_label:
      previewRow?.property_label ?? manualAddress?.label ?? "Objektadresse des Terminbriefs",
    property_address: previewRow?.property_address ?? manualAddress,
    appointment_label: previewRow?.appointment_label ?? "Termin aus Terminbrief",
    default_mode: selection.print_mode,
    available_modes: previewRow?.available_modes.some(
      (option) => option.mode === selection.print_mode,
    )
      ? previewRow.available_modes
      : [
          ...(previewRow?.available_modes ?? []),
          {
            mode: selection.print_mode,
            label: "Gespeicherte Druckart",
            disabled_reason: null,
          },
        ],
    recipient_options:
      existingRecipient &&
      !previewRow?.recipient_options.some(
        (option) =>
          option.customer_id === existingRecipient.customer_id &&
          option.contact_point_id === existingRecipient.contact_point_id,
      )
        ? [
            ...(previewRow?.recipient_options ?? []),
            {
              customer_id: existingRecipient.customer_id,
              contact_point_id: existingRecipient.contact_point_id,
              label: "Gespeicherter Empfänger",
              postal_address: null,
            },
          ]
        : (previewRow?.recipient_options ?? []),
    selected_recipient_customer_id:
      existingRecipient?.customer_id ?? objectRecipient?.customer_id ?? null,
    selected_recipient_contact_point_id: existingRecipient?.contact_point_id ?? null,
  }
}

const rowBinding = (
  actionCaseId: string,
  context: LetterDeliveryDecisionPreviewRowContext | null,
  selection: LetterDeliverySelection | null,
  disabled: boolean,
  disabledReason: string | null,
): PrintWorkflowRowBinding | undefined =>
  context?.work_order_id && selection
    ? {
        id: actionCaseId,
        workOrderId: context.work_order_id,
        contactOrderStepId: context.contact_order_step_id,
        recipientCustomerId:
          selection.recipient.kind === "existing_contact" ||
          selection.recipient.kind === "customer_at_property_address"
            ? selection.recipient.customer_id
            : null,
        recipientContactPointId:
          selection.recipient.kind === "existing_contact"
            ? selection.recipient.contact_point_id
            : null,
        recipientSource:
          selection.recipient.kind === "existing_contact"
            ? "CUSTOMER_CONTACT_ADDRESS"
            : selection.recipient.kind === "customer_at_property_address"
              ? "OBJECT_ADDRESS"
              : selection.recipient.kind === "property_address"
                ? "IMPERSONAL"
                : null,
        recipientName:
          selection.recipient.kind === "customer_at_property_address"
            ? (selection.recipient.name ?? null)
            : null,
        defaultMode: selection.print_mode,
        letterDeliveryBatchItemId: selection.letter_delivery_batch_item_id,
        actionCaseId,
        disabled,
        disabledReason,
      }
    : undefined

const estimateStateForRow = ({
  selection,
  context,
  printPreview,
  previewLoading,
  previewError,
  previewMismatchReason,
}: {
  selection: LetterDeliverySelection | null
  context: LetterDeliveryDecisionPreviewRowContext | null
  printPreview: WorkOrderPrintPreviewRow | null
  previewLoading: boolean
  previewError: string | null
  previewMismatchReason: string | null
}): DeliveryEstimateState => {
  if (previewLoading) return { status: "loading" }

  if (!selection) {
    return {
      status: "error",
      detail: "Gespeicherte Briefauswahl fehlt oder ist ungültig.",
    }
  }

  if (previewMismatchReason) {
    return { status: "error", detail: previewMismatchReason }
  }

  if (previewError) return { status: "error", detail: previewError }

  if (!context || !printPreview) {
    return { status: "error", detail: "Vorschaudaten fehlen." }
  }

  if (!context.work_order_id) {
    return { status: "error", detail: "Auftrag des Terminbriefs fehlt." }
  }

  if (context.work_order_id !== printPreview.work_order_id) {
    return {
      status: "error",
      detail: "Zustellvorschau passt nicht zum Auftrag des Terminbriefs.",
    }
  }

  return {
    status: "ready",
    itemId: context.letter_delivery_batch_item_id,
  }
}

const buildRow = ({
  item,
  step,
  context,
  printPreview,
  previewError,
  previewLoading,
  outcome,
}: {
  item: InboxWorkflowItem
  step: InboxWorkflowStep
  context: LetterDeliveryDecisionPreviewRowContext | null
  printPreview: WorkOrderPrintPreviewRow | null
  previewError: string | null
  previewLoading: boolean
  outcome: InboxWorkflowRowOutcomeMap[string] | null
}): LetterDeliveryDecisionRow => {
  const actionCaseId = item.action_case.id
  const ignored = recordValue(projectedStep(item, SELECTION_STEP_KEY)?.output)?.action === "ignore"
  const resolved = itemIsResolved(item, step)
  const open = itemIsOpen(item, step)
  const selection = parseDurableSelection(projectedStep(item, SELECTION_STEP_KEY)?.output)
  const deliveryDecision = parseDecision(projectedStep(item, DECISION_STEP_KEY)?.output)
  const selectionOutputError = selection
    ? null
    : "Gespeicherte Briefauswahl fehlt oder ist ungültig."
  const decisionOutputError =
    resolved &&
    (!deliveryDecision ||
      !selection ||
      deliveryDecision.letter_delivery_batch_item_id !== selection.letter_delivery_batch_item_id)
      ? "Gespeicherte Zustellentscheidung fehlt oder ist ungültig."
      : null
  const previewMismatchReason =
    context &&
    selection &&
    (context.letter_delivery_batch_item_id !== selection.letter_delivery_batch_item_id ||
      context.selection.letter_delivery_batch_item_id !== selection.letter_delivery_batch_item_id)
      ? "Zustellvorschau passt nicht zur gespeicherten Briefauswahl."
      : null
  const activePreviewError = open
    ? (previewMismatchReason ??
      previewError ??
      (!context ? (previewLoading ? "Vorschau wird geladen." : "Vorschaudaten fehlen.") : null))
    : null
  const outputError = ignored ? null : (selectionOutputError ?? decisionOutputError)
  const disabledReason =
    outcome?.error ??
    outputError ??
    activePreviewError ??
    item.error ??
    (!open ? (resolved ? "Bereits abgeschlossen." : "Dieser Schritt ist nicht offen.") : null)
  const disabled =
    !item.compatible ||
    !open ||
    Boolean(outputError) ||
    Boolean(activePreviewError) ||
    outcome?.status === "success"
  const print = printRowFromContext(actionCaseId, context, selection, printPreview)
  const estimateState = estimateStateForRow({
    selection,
    context,
    printPreview,
    previewLoading,
    previewError,
    previewMismatchReason,
  })
  const automaticOption = context?.delivery_mode_options.find(
    (option) => option.mode === "automatic",
  )
  const currentAllowedDeliveryModes = context?.delivery_mode_options
    .filter((option) => !option.disabled_reason)
    .map((option) => option.mode) ?? ["manual"]
  const allowedDeliveryModes =
    resolved &&
    deliveryDecision &&
    !currentAllowedDeliveryModes.includes(deliveryDecision.delivery_mode)
      ? [...currentAllowedDeliveryModes, deliveryDecision.delivery_mode]
      : currentAllowedDeliveryModes

  return {
    ...print,
    appointment: context?.appointment,
    communication_presentation: context?.communication_presentation,
    id: actionCaseId,
    actionCaseId,
    workflowItem: item,
    step: projectedStep(item, step.step_key),
    rowOutcome: outcome,
    isResolved: resolved,
    isOpen: open,
    isCompatible: item.compatible,
    letterDeliveryBatchItemId: selection?.letter_delivery_batch_item_id ?? null,
    selection,
    deliveryDecision,
    allowedDeliveryModes,
    automaticDeliveryDisabledReason: automaticOption?.disabled_reason ?? null,
    estimateState,
    binding: rowBinding(actionCaseId, context, selection, disabled, disabledReason),
    disabled,
    disabledReason,
    statusLabel: ignored
      ? "Bewusst ignoriert"
      : outcome?.status === "failed"
        ? "Prüfung nötig"
        : outputError
          ? "Datenfehler"
          : resolved || outcome?.status === "success"
            ? "Erledigt"
            : activePreviewError || !item.compatible
              ? "Nicht verfügbar"
              : "Offen",
    statusDetail:
      outcome?.error || outcome?.detail || outputError || activePreviewError || item.error || null,
    searchText: [
      print.work_order_label,
      print.property_label,
      print.appointment_label,
      ...print.recipient_options.map((option) => option.label),
    ]
      .filter(Boolean)
      .join(" "),
  }
}

export const buildLetterDeliveryDecisionRows = ({
  workflow,
  step,
  preview,
  rowOutcomes,
  previewLoading = false,
  previewError = null,
}: {
  workflow: InboxWorkflowState
  step: InboxWorkflowStep
  preview: LetterDeliveryDecisionPreviewPayload | null
  rowOutcomes: InboxWorkflowRowOutcomeMap
  previewLoading?: boolean
  previewError?: string | null
}): LetterDeliveryDecisionRow[] =>
  workflow.items.map((item) => {
    const actionCaseId = item.action_case.id
    const context = preview?.row_context_by_action_case_id[actionCaseId] ?? null

    return buildRow({
      item,
      step,
      context,
      printPreview: context
        ? (preview?.shared.print_previews_by_id[context.print_preview_id] ?? null)
        : null,
      previewError: preview?.row_errors_by_action_case_id[actionCaseId] ?? previewError,
      previewLoading,
      outcome: rowOutcomes[actionCaseId] ?? null,
    })
  })

const stateFromSelection = (
  row: LetterDeliveryDecisionRow,
  selection: LetterDeliverySelection,
): WorkOrderPrintRowState => {
  const base = buildPrintRowState(row, selection.print_mode)
  const recipient = selection.recipient

  if (recipient.kind === "existing_contact") {
    return {
      ...base,
      mode: selection.print_mode,
      recipientValue: `${recipient.customer_id}:${recipient.contact_point_id}`,
      recipientCustomerId: recipient.customer_id,
      recipientContactPointId: recipient.contact_point_id,
      recipientSource: "CUSTOMER_CONTACT_ADDRESS",
      recipientName: base.recipientName || "Gespeicherter Empfänger",
      recipientAddress: base.recipientAddress,
      personalConfigConfirmed: true,
    }
  }

  if (recipient.kind === "customer_at_property_address") {
    return {
      ...base,
      mode: selection.print_mode,
      recipientValue: objectAddressRecipientValue,
      recipientCustomerId: recipient.customer_id,
      recipientContactPointId: null,
      recipientSource: "OBJECT_ADDRESS",
      recipientName: recipient.name ?? row.binding?.recipientName ?? null,
      recipientAddress: row.property_address,
      propertyAddress: row.property_address,
      personalConfigConfirmed: true,
    }
  }

  if (recipient.kind === "manual_address") {
    return {
      ...base,
      mode: selection.print_mode,
      recipientValue: manualRecipientValue,
      recipientCustomerId: null,
      recipientContactPointId: null,
      recipientSource: "MANUAL_ADDRESS",
      recipientName: recipient.name,
      recipientAddress: recipient.address,
      personalConfigConfirmed: true,
    }
  }

  return {
    ...base,
    mode: selection.print_mode,
    recipientValue: impersonalRecipientValue,
    recipientCustomerId: null,
    recipientContactPointId: null,
    recipientSource: "IMPERSONAL",
    recipientName: null,
    recipientAddress: null,
    personalConfigConfirmed: true,
  }
}

export const createLetterDeliveryDecisionDraft = (
  row: LetterDeliveryDecisionRow,
  automaticLetterDeliveryAllowed = true,
): LetterDeliveryDecisionDraft => {
  const printState = row.selection
    ? stateFromSelection(row, row.selection)
    : buildPrintRowState(row)

  return {
    ...printState,
    ignored: row.statusLabel === "Bewusst ignoriert",
    personalConfigConfirmed: true,
    deliveryMode: row.isResolved
      ? (row.deliveryDecision?.delivery_mode ?? "")
      : (row.deliveryDecision?.delivery_mode ??
        (automaticDeliveryUnavailableReason(row, automaticLetterDeliveryAllowed)
          ? "manual"
          : "automatic")),
  }
}
